'use client'

import type { MouseEventHandler, ReactNode } from 'react'

import Button from 'react-bootstrap/Button'

import Link from '@/components/Link'

interface LinkButtonProps {
  text: ReactNode
  onClick?: MouseEventHandler<HTMLButtonElement>
  href?: string
  /** A PDF URL to open on click (lets a Server Component pass a string, not a handler). */
  pdf?: string
}

function LinkButton({ text, onClick, href, pdf }: LinkButtonProps) {
  // A `pdf` is a real document URL, so render an actual anchor (opening in a new
  // tab) instead of a JS-driven button. This gives a hoverable href, middle/⌘-click
  // and "copy link", while still letting a Server Component pass a plain string.
  // An internal `href` renders as a prefetching Next <Link>.
  const linkAs = pdf ? 'a' : href ? Link : undefined
  const linkHref = pdf ?? href
  return (
    <>
      <style type="text/css">
        {`
        .btn-secondary {
          color: #0900CB;
          background-color: white;
          padding: 12px 20px;
          font-size: 16px;
          border: 1px solid var(--gradient-brand-80060045-deg, #070097);
          border-radius: 6px;
          font-size: 16px;
          font-weight: 500;
          box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
        }
        .btn-secondary:hover {
          background: linear-gradient(45deg, #070097 0%, #211AFB 100%);
          color: white;
          transform: none;
        }
        @media (max-width: 480px) {
        .btn-secondary {
          width: 100%;
        }
      }

        `}
      </style>

      {/* A `pdf`/`href` renders as a real anchor styled as the button; otherwise a plain button. */}
      <Button
        variant="secondary"
        onClick={linkHref ? undefined : onClick}
        // react-bootstrap's polymorphic `as` is unsatisfiable for components under
        // @types/react 19 (it resolves to `As & keyof IntrinsicElements`). The
        // runtime render (an <a> with the btn classes) is correct.
        // @ts-expect-error -- known react-bootstrap × React 19 typings gap
        as={linkAs}
        href={linkHref}
        target={pdf ? '_blank' : undefined}
        rel={pdf ? 'noopener noreferrer' : undefined}
      >
        {text}
      </Button>
    </>
  )
}

export default LinkButton
